import { Dialog, DialogContent, Divider, Typography } from "@mui/material";
import CustomPrimaryButton from "@fuse/core/CustomPrimaryButton/CustomPrimaryButton";
import useThemeMediaQuery from "@fuse/hooks/useThemeMediaQuery";

interface RecoverActivateModalProps {
	petName: string;
	chip: string;
	onActivate: () => void;
}

function RecoverActivateModal({ petName, chip, onActivate }: RecoverActivateModalProps) {
	const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down("sm"));

	const content = (
		<div className="flex flex-col gap-6">
			{/* Logo */}
			<div className="flex justify-center">
				<img src="assets/img/checkout-logo.svg" alt="Peeva" style={{ width: 160 }} />
			</div>

			{/* Title */}
			<div className="flex flex-col items-center gap-1">
				<Typography variant="h6" fontWeight={700} align="center" sx={{ fontSize: 26 }}>
					Microchip Record Found —{"\n"}Owner Verification Required
				</Typography>
				<Typography variant="caption" align="center" color="text.secondary">
					Retrieved from the Peeva North American pet recovery network
				</Typography>
			</div>

			{/* Pet Record Box */}
			<div style={{ border: "1px solid #ccc", borderRadius: 8, padding: "16px" }}>
				<div className="flex flex-col gap-2">
					<Typography variant="body2" fontWeight={700} sx={{ fontSize: 15 }}>Pet Record</Typography>
					<Typography variant="body2" sx={{ fontSize: 15 }}>Pet: {petName}</Typography>
					<Typography variant="body2" sx={{ fontSize: 15 }}>Microchip ID: <strong>{chip}</strong></Typography>
					<Typography variant="body2" sx={{ fontSize: 15, color: "#ED6C02" }}>
						Status: Owner Record Not Verified
					</Typography>
				</div>
			</div>

			{/* Body paragraphs */}
			<div className="flex flex-col">
				<Typography variant="body2" sx={{ py: 1.5 }}>
					This microchip ID exists in our system with no verified owner currently on record. Verification ensures veterinarians and shelters can access your contact information through the Peeva pet recovery system.
				</Typography>
				</div>

			{/* Pricing */}
			<div className="flex flex-col items-center gap-3">
				{/* Lifetime divider row */}
				<div className="flex items-center gap-3 w-full">
					<Divider sx={{ flex: 1 }} />
					<Typography variant="body1" fontWeight={500} sx={{ whiteSpace: "nowrap" }}>
						Lifetime Microchip Registration
					</Typography>
					<Divider sx={{ flex: 1 }} />
				</div>

				<Typography variant="h4" fontWeight={700}>
					$104{" "}
					<Typography component="span" variant="body1">one-time activation</Typography>
				</Typography>
				<Typography variant="caption" color="text.secondary" align="center">
					Partial microchip registration detected.<br />Reduced activation fee applied.
				</Typography>
			</div>

			{/* CTA */}
			<CustomPrimaryButton onClick={onActivate} fullWidth>
				Verify {petName}'s Owner Record
			</CustomPrimaryButton>

			{/* Payment icons */}
			<div className="flex justify-center gap-3 flex-wrap">
				{[
					{ src: "assets/images/payment/37.png", alt: "Apple Pay" },
					{ src: "assets/images/payment/38.png", alt: "Google Pay" },
					{ src: "assets/images/payment/39.png", alt: "Visa" },
					{ src: "assets/images/payment/40.png", alt: "Mastercard" },
					{ src: "assets/images/payment/44.png", alt: "Klarna" },
				].map(({ src, alt }) => (
					<img key={alt} src={src} alt={alt} style={{ height: 28, width: "auto" }} />
				))}
			</div>

			{/* Stripe notice */}
			<Typography variant="caption" align="center" color="text.secondary">
				Secure checkout powered by{" "}
				<span style={{ color: "#635BFF", fontWeight: 700 }}>Stripe</span>
			</Typography>
		</div>
	);

	if (isMobile) {
		return (
			<div
				style={{
					position: "fixed",
					inset: 0,
					overflowY: "auto",
					background: "#fff",
					zIndex: 1300,
					padding: "24px 16px",
				}}
			>
				<div className="max-w-sm mx-auto">
					{content}
				</div>
			</div>
		);
	}

	return (
		<Dialog open maxWidth="sm" fullWidth disableEscapeKeyDown
			PaperProps={{ sx: { borderRadius: 3, p: 2 } }}>
			<DialogContent>{content}</DialogContent>
		</Dialog>
	);
}

export default RecoverActivateModal;
